import { ComponentCO2, ComponentCO2Link, IAccount, User } from '@acies/domain';
import {
	ComponentCO2LinkResponseSchema,
	ComponentCO2LinkSchema,
	ComponentCO2ResponseSchema,
	ComponentCO2Schema,
	IComponentCO2CreateSchema,
	IComponentCO2LinkBatchCreateSchema,
	IComponentCO2LinkCreateSchema,
	IComponentCO2LinkResponseSchema,
	IComponentCO2LinkSchema,
	IComponentCO2ResponseSchema,
	IComponentCO2Schema,
	IComponentCO2UpdateSchema,
	IUsers,
	UsersSchema,
} from '@acies/schema';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import AuthorizationError from '@shared/errors/authorization-error';
import ConflictError from '@shared/errors/conflict-error';
import InternalServerError from '@shared/errors/internal-server-error';
import NetworkError from '@shared/errors/network-error';
import ParseError from '@shared/errors/parse-error';
import UnknownError from '@shared/errors/unknown-error';
import { SessionStorageService } from '@shared/services/storage.service';
import { UserService } from '@shared/services/user.service';
import { catchError, map, Observable, of, retry, switchMap } from 'rxjs';
import { ZodType } from 'zod';
import SearchCO2LinkResult from '../domain/search-co2link-result';
import { schemaToComponentCO2, schemaToComponentCO2Link, schemaToComponentCO2LinkList, schemaToComponentCO2List, schemaToUsers } from './transform';

interface IStoredEnvironment {
	engineId?: unknown;
}

@Injectable({
	providedIn: 'root',
})
export default class CO2Service {
	#httpClient: HttpClient;
	#baseURL: string;
	#users: Map<string, User>;
	#userService: UserService;
	#sessionStorageService: SessionStorageService;
	#defaultEngine: string | null;
	public constructor(httpClient: HttpClient, userService: UserService, sessionStorageService: SessionStorageService) {
		this.#httpClient = httpClient;
		this.#userService = userService;
		this.#sessionStorageService = sessionStorageService;
		this.#defaultEngine = null;
		this.#users = new Map();
		this.#baseURL = `${environment.awsEnvUri}/mdm/apps/one/stores/`;
		this.#userService.account$.subscribe(this.#accountChanged.bind(this));
	}

	#accountChanged(value: IAccount | null): void {
		if (typeof value === 'object' && 'defaultEngine' in value && typeof value['defaultEngine'] === 'string') {
			this.#defaultEngine = value['defaultEngine'].length > 0 ? value['defaultEngine'] : null;
		} else {
			this.#defaultEngine = null;
		}
		this.#users.clear();
	}

	public deleteComponentCO2(value: ComponentCO2) {
		const headers: HttpHeaders = this.#userService.getHeaders();
		return this.#httpClient
			.delete<ComponentCO2>(`${environment.awsEnvUri}${value.self}`, {
				headers,
			})
			.pipe(this.#forwardComponentCO2(value), catchError(this.#handleError));
	}

	public deleteComponentCO2Link(value: ComponentCO2Link) {
		const headers: HttpHeaders = this.#userService.getHeaders();
		return this.#httpClient
			.delete<ComponentCO2Link>(`${environment.awsEnvUri}${value.self}`, {
				headers,
			})
			.pipe(this.#forwardComponentCO2Link(value), catchError(this.#handleError));
	}

	#forwardComponentCO2(value: ComponentCO2) {
		return map(() => value);
	}

	#forwardComponentCO2Link(value: ComponentCO2Link) {
		return map(() => value);
	}

	public updateComponentCO2(value: IComponentCO2UpdateSchema, version = 1): Observable<ComponentCO2 | AuthorizationError | ConflictError | InternalServerError | NetworkError | ParseError | UnknownError | null> {
		let headers: HttpHeaders = this.#userService.getHeaders();
		headers = headers.append('If-Match', `"${version}"`);
		const url = this.#storeUrl(`component-co2/${value.name}`);
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.put<ComponentCO2 | null>(url, value, {
				headers,
			})
			.pipe(retry(1), this.#parseUpdate(ComponentCO2Schema), this.#transformComponentCO2Update(), catchError(this.#handleError));
	}

	public createComponentCO2(value: IComponentCO2CreateSchema): Observable<ComponentCO2 | AuthorizationError | ConflictError | InternalServerError | NetworkError | ParseError | UnknownError> {
		let headers: HttpHeaders = this.#userService.getHeaders();
		headers = headers.append('If-None-Match', `*`);
		const url = this.#storeUrl(`component-co2/${value.name}`);
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.put<ComponentCO2>(url, value, {
				headers,
			})
			.pipe(retry(1), this.#parse(ComponentCO2Schema), this.#transformComponentCO2(), catchError(this.#handleError));
	}

	public batchCreateComponentCO2Links(value: IComponentCO2LinkBatchCreateSchema): Observable<string[] | ParseError | NetworkError | AuthorizationError | ConflictError | InternalServerError | UnknownError> {
		const headers: HttpHeaders = this.#userService.getHeaders();
		const url = this.#storeUrl(`component-co2/${value.name}`);
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.put<null>(url, value, {
				headers,
			})
			.pipe(retry(1), this.#forwardBatchLinks(value.links), catchError(this.#handleError));
	}

	#forwardBatchLinks(value: string[]) {
		return map(() => value);
	}

	public createComponentCO2Link(value: IComponentCO2LinkCreateSchema): Observable<ComponentCO2Link | AuthorizationError | ConflictError | InternalServerError | NetworkError | ParseError | UnknownError | null> {
		const headers: HttpHeaders = this.#userService.getHeaders();
		const url = this.#storeUrl(`component-co2link/${value.name}`);
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.put<ComponentCO2Link>(url, value, {
				headers,
			})
			.pipe(retry(1), this.#parseUpdate(ComponentCO2LinkSchema), this.#transformComponentCO2LinkUpdate(), catchError(this.#handleError));
	}

	public listComponentCO2(): Observable<ComponentCO2[] | AuthorizationError | InternalServerError | NetworkError | ParseError | UnknownError> {
		if (!this.#engineId()) {
			return of(new UnknownError());
		}

		return this.#loadUsers().pipe(
			switchMap((users) => {
				if (users instanceof Map) {
					this.#users = users;
					return this.#loadComponents(users);
				} else {
					return of(users);
				}
			}),
		);
	}

	#loadUsers() {
		return this.#userService.fetchUsers().pipe(this.#parse(UsersSchema), this.#transformUsers(), catchError(this.#handleError));
	}

	#loadComponents(users: Map<string, User>): Observable<ComponentCO2[] | AuthorizationError | InternalServerError | NetworkError | ParseError | UnknownError> {
		const url = this.#storeUrl('component-co2?limit=500');
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.get<[]>(url, {
				headers: this.#userService.getHeaders(),
			})
			.pipe(retry(1), this.#parse(ComponentCO2ResponseSchema), this.#transformComponents(users), catchError(this.#handleError));
	}

	public listComponentCO2Link(value: ComponentCO2, offset: null | number = null, limit: null | number = 500): Observable<ComponentCO2Link[] | AuthorizationError | InternalServerError | NetworkError | ParseError | UnknownError> {
		const url = this.#storeUrl(`component-co2link?co2=${value.name}${offset === null ? `` : `&offset=${offset}`}${limit === null ? `` : `&limit=${limit}`}`);
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.get<[]>(url, {
				headers: this.#userService.getHeaders(),
			})
			.pipe(retry(1), this.#parse(ComponentCO2LinkResponseSchema), this.#transformComponentCO2Links(), catchError(this.#handleError));
	}

	public searchCO2Link(term: string) {
		const WILD_CARD = '*';
		const MAX_LIMIT = 100;
		const lowerTrimmed = term.toLowerCase().trim();
		const encoded = encodeURIComponent(lowerTrimmed);
		const url = this.#storeUrl(`component-co2link?name=${encoded}${WILD_CARD}&limit=${MAX_LIMIT}`);
		if (!url) {
			return of(new UnknownError());
		}
		return this.#httpClient
			.get<[]>(url, {
				headers: this.#userService.getHeaders(),
			})
			.pipe(retry(1), this.#parse(ComponentCO2LinkResponseSchema), this.#transformComponentCO2LinkSearch(), catchError(this.#handleError));
	}

	#transformComponents(users: Map<string, User>) {
		return map((data: IComponentCO2ResponseSchema) => schemaToComponentCO2List(data, users));
	}

	#transformComponentCO2Links() {
		return map((data: IComponentCO2LinkResponseSchema) => schemaToComponentCO2LinkList(data));
	}

	#transformComponentCO2LinkSearch() {
		return map((data: IComponentCO2LinkResponseSchema) => {
			const items = schemaToComponentCO2LinkList(data);
			const total = data.total;
			return new SearchCO2LinkResult({ items, total });
		});
	}

	#transformComponentCO2() {
		return map((data: IComponentCO2Schema) => schemaToComponentCO2(data, this.#users));
	}

	#transformComponentCO2Update() {
		return map((data: IComponentCO2Schema | null) => {
			if (data === null) {
				return null;
			}
			return schemaToComponentCO2(data, this.#users);
		});
	}

	#transformComponentCO2LinkUpdate() {
		return map((data: IComponentCO2LinkSchema | null) => {
			if (data === null) {
				return null;
			}
			return schemaToComponentCO2Link(data);
		});
	}

	#transformUsers() {
		return map((data: IUsers) => schemaToUsers(data));
	}

	#parse<T>(schema: ZodType<T>) {
		return map((data: unknown) => {
			const parsed = schema.safeParse(data);
			if (!parsed.success) {
				return new ParseError(parsed.error.message);
			}
			return parsed.data;
		});
	}

	#parseUpdate<T>(schema: ZodType<T>) {
		return map((data: unknown) => {
			if (data === null) {
				return null;
			}
			const parsed = schema.safeParse(data);
			if (!parsed.success) {
				return new ParseError(parsed.error.message);
			}
			return parsed.data;
		});
	}

	#handleError(error: HttpErrorResponse | ParseError): Observable<NetworkError | AuthorizationError | ConflictError | InternalServerError | ParseError | UnknownError> {
		if (error instanceof ParseError) {
			return of(error);
		}
		if (error.status === 0) {
			return of(new NetworkError());
		}
		if (error.status === 401) {
			return of(new AuthorizationError());
		}
		if (error.status === 409 || error.status === 412) {
			return of(new ConflictError());
		}
		if (error.status === 500 || error.status === 502) {
			return of(new InternalServerError());
		}
		return of(new UnknownError());
	}

	#storeUrl(path: string): string | null {
		const engineId = this.#engineId();
		return engineId ? `${this.#baseURL}${engineId}/${path}` : null;
	}

	#engineId(): string | null {
		const selectedEnvironment = this.#selectedEnvironment();
		if (selectedEnvironment?.engineId && this.#allowedEngineIds().has(selectedEnvironment.engineId)) {
			return selectedEnvironment.engineId;
		}

		return this.#defaultEngine;
	}

	#selectedEnvironment(): { engineId?: string } | null {
		const value = this.#sessionStorageService.get('User.SelectedEnvironment');
		if (!value) {
			return null;
		}

		try {
			const parsed = JSON.parse(value) as IStoredEnvironment;
			return typeof parsed.engineId === 'string' && parsed.engineId.length > 0 ? { engineId: parsed.engineId } : null;
		} catch {
			return null;
		}
	}

	#allowedEngineIds(): Set<string> {
		const value = this.#sessionStorageService.get('User.Environments');
		if (!value) {
			return new Set();
		}

		try {
			const parsed = JSON.parse(value) as unknown;
			if (!Array.isArray(parsed)) {
				return new Set();
			}

			return new Set(parsed.map((environment) => this.#storedEnvironmentEngineId(environment)).filter((engineId): engineId is string => engineId !== null));
		} catch {
			return new Set();
		}
	}

	#storedEnvironmentEngineId(environment: unknown): string | null {
		if (typeof environment !== 'object' || environment === null || Array.isArray(environment)) {
			return null;
		}

		const { engineId } = environment as IStoredEnvironment;
		return typeof engineId === 'string' && engineId.length > 0 ? engineId : null;
	}
}
